/**
 * Catalog documents API utilities.
 *
 * Always-private supporting documents (mockup `Additional Info` > Documents).
 * Documents never appear on the public catalog detail by construction —
 * the public API does not return them.
 */

import api from './api';

export type DocumentCategory =
  | 'appraisal'
  | 'receipt'
  | 'provenance'
  | 'certificate_of_authenticity'
  | 'insurance'
  | 'conservation_report'
  | 'exhibition_record'
  | 'auction_record'
  | 'other';

export interface CatalogDocument {
  docId: string;
  catalogId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  category: DocumentCategory;
  s3Key: string;
  uploadedAt: string;
  status: 'pending' | 'ready';
}

export interface InitiateUploadResponse {
  docId: string;
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export const initiateDocumentUpload = async (
  catalogId: string,
  body: { filename: string; contentType: string; sizeBytes: number; category: DocumentCategory },
  ownerId?: string
): Promise<InitiateUploadResponse> => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await api.post(`/api/catalogs/${catalogId}/documents/initiate${params}`, body);
  return response.data;
};

export const completeDocumentUpload = async (
  catalogId: string,
  docId: string,
  ownerId?: string
): Promise<CatalogDocument> => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await api.post(`/api/catalogs/${catalogId}/documents/${docId}/complete${params}`);
  return response.data;
};

export const listDocuments = async (catalogId: string, ownerId?: string): Promise<CatalogDocument[]> => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await api.get(`/api/catalogs/${catalogId}/documents${params}`);
  return response.data.documents ?? [];
};

export const deleteDocument = async (catalogId: string, docId: string, ownerId?: string): Promise<void> => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  await api.delete(`/api/catalogs/${catalogId}/documents/${docId}${params}`);
};

export const getDocumentDownloadUrl = async (
  catalogId: string,
  docId: string,
  ownerId?: string
): Promise<string> => {
  const params = ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : '';
  const response = await api.get(`/api/catalogs/${catalogId}/documents/${docId}/download${params}`);
  return response.data.downloadUrl as string;
};
